import { UserPrivilegedGroupNames } from '../constants';
import { LegalLocation, Seniority, Stream } from './accounts';
import { BaseEntityModel } from './baseEntityModel';
import { IdField, TimestampsFields } from './generic';
import { CompensationType } from './reports';

// --- --- --- --- --- users types from FSS --- --- --- --- ---
export interface CreateUserDto {
  email: string;
  fullName: string;
  avatarUrl: string | null;
  zohoId: string;
  zohoEmployeeId: string;
  country: string;
  legalLocation: LegalLocation;
  city: string;
  designation: string;
  stream: Stream;
  seniority: Seniority;
  senioritySow: string | null;
  employmentType: string;
  departmentId: string;
  reportingToId?: string;
  compensationRate?: number;
  compensationType?: CompensationType;
  isBillable?: boolean | null;
  paymentOption?: string | null;
}

export interface UserDto extends CreateUserDto, TimestampsFields, IdField {
  deactivatedAt: Date | null;
}

export type UserFullDto = UserDto;

export type UserProfileDto = Pick<
  UserFullDto,
  | 'id'
  | 'zohoId'
  | 'email'
  | 'fullName'
  | 'avatarUrl'
  | 'legalLocation'
  | 'designation'
  | 'stream'
>;

// --- --- --- --- --- FE-only types (deprecated, cause I plan to move into a copy-pasting practice from FSS / RF) --- --- --- --- ---

export interface UserBaseModel extends BaseEntityModel {
  email: string;
  fullName: string;
}

export interface User extends UserBaseModel {
  avatarUrl: string;
  city: string;
  country: string;
  departmentId: string;
  designation: string;
  createdAt: string;
  deletedAt: string | null;
  deactivatedAt: string | null;
  updatedAt: string;
  zohoId: string;
  legalLocation: string;
}

export interface UserWithPermissions extends User {
  permissions: UserPermissions;
}

export interface UserForSearch extends User {
  names: string[];
}

// --- Account & Project search items (for unified sidebar search) ---

export interface AccountSearchItem {
  kind: 'account';
  id: string;
  name: string;
  names: string[]; // for Fuse.js
}

export interface ProjectSearchItem {
  kind: 'project';
  id: string;
  name: string;
  accountId: string; // FK — used to build the portfolio URL
  names: string[]; // for Fuse.js
}

// Union of all three searchable result types.
// UserForSearch has no `kind` field — use type guards below to distinguish.
export type SearchItem = UserForSearch | AccountSearchItem | ProjectSearchItem;

export function isAccountSearchItem(
  item: SearchItem,
): item is AccountSearchItem {
  return (item as AccountSearchItem).kind === 'account';
}

export function isProjectSearchItem(
  item: SearchItem,
): item is ProjectSearchItem {
  return (item as ProjectSearchItem).kind === 'project';
}

export function isUserSearchItem(item: SearchItem): item is UserForSearch {
  const k = (item as AccountSearchItem | ProjectSearchItem).kind;
  return k !== 'account' && k !== 'project';
}

export interface MassEditUser extends User, UserForUserVerifyPermissions {
  worstCase: number | null;
  bestCase: number | null;
}

export interface UserForUserPermissionsData
  extends User,
    UserForUserVerifyPermissions {}

export interface UserForUserVerifyPermissions {
  canModifyMetrics?: boolean;
  canEditHistoricalData?: boolean;
}

export interface FSSRole {
  clientPartner: boolean;
  manager: boolean;
}

export interface UserPermissions {
  canViewListOfContractors: boolean;
  canManageKpiDefinitions: boolean;
  canClosePeriod: boolean;
  canAdjustKpiValues: boolean;
  canApproveKpiValues: boolean;
  canSubmitKpiValues: boolean;
  canGenerateReport: boolean;
  canViewMyMetrics: boolean;
  hasAccounts: boolean;
  isFinanceManager: boolean;
  isFinanceAdmin: boolean;
  isFinance: boolean;
  isCompensationManager: boolean;
  isBillRateManager: boolean;
  isRevenueSummaryAdmin: boolean;
  canEditHistoricalData: boolean;
  seesAllAccounts: boolean;
}

export interface UserAccountsCountDto {
  accountsCount: number;
  roles: UserPrivilegedGroupNames[];
}

// Rocky User Types

export interface RockyMinimalUser {
  id: string;
  name: string;
  email: string;
  position: string;
  imageUrl: string;
  city: string;
  country: string;
}

export interface RockyUser {
  id: string;
  name: string;
  email: string;
  position: string;
  imageUrl: string;
  city: string;
  country: string;
  primaryDepartment: {
    id: string;
    name: string;
  };
  seniority: string;
  account: string;
  clientPartner: RockyMinimalUser;
  primaryReportsTo: RockyMinimalUser;
  secondaryReportsTo: RockyMinimalUser;
  engineeringManager: RockyMinimalUser;
  locationHr: RockyMinimalUser;
  zohoId: string;
  peoplePartner: RockyMinimalUser;
  officeCountry: string;
  departments: [
    [
      {
        id: string;
        name: string;
      },
    ],
  ];
  rockyRoles: [null];
  permissions: [null];
  hasReporters: true;
  billingStatus: 'Billable';
  officeCity: string;
  manager: RockyMinimalUser;
  portfolioDeliveryManager: RockyMinimalUser;
  locationLastCheckedAt: Date;
  isSharedLocation: boolean;
  hasAvailableEvaluationsExceptOwn: boolean;
  hasAvailableHappinessExceptOwn: boolean;
  latestTermsOfUseVersionAccepted: string;
}

export interface TermsOfUserAcceptResp {
  version: string;
  acceptedAt: Date;
}

export interface UserData {
  id: string;
  zohoId: string;
  email: string;
  fullName: string;
  avatarUrl: string;
  legalLocation: string;
  designation: string;
  seniority: string;
  stream: string;
  reportingTo?: UserData;
}
